/**
 * OrchestratorProvider component for managing agent coordination in the frontend.
 * 
 * This component provides a React context for accessing the orchestrator,
 * which manages communication between different agents and handles
 * configuration settings.
 */import React,{createContext,useContext,useState,useEffect}from'react';import axios from'axios';// Define the context type
import{jsx as _jsx}from"react/jsx-runtime";// Create the context with a default value
const OrchestratorContext=/*#__PURE__*/createContext(null);// Props for the OrchestratorProvider component
/**
 * OrchestratorProvider component for providing orchestrator functionality.
 * 
 * @param props Component props
 * @returns React component
 */export const OrchestratorProvider=_ref=>{let{children,serverPort=8080,frontendPort=3000}=_ref;// State for tracking connection status and registered agents
const[isConnected,setIsConnected]=useState(false);const[agents,setAgents]=useState({});// API URL based on server port
const apiUrl=`http://localhost:${serverPort}`;// Check connection to the server on mount and when server port changes
useEffect(()=>{const checkConnection=async()=>{try{const response=await axios.get(`${apiUrl}/api/health`);setIsConnected(response.status===200);}catch(error){console.error('Failed to connect to server:',error);setIsConnected(false);}};checkConnection();// Set up periodic connection check
const interval=setInterval(checkConnection,30000);return()=>{clearInterval(interval);};},[apiUrl]);/**
   * Send a message to an agent via the server.
   * 
   * @param agentType Type of the agent to send the message to
   * @param message Message content
   * @returns Promise resolving to the agent's response
   */const sendMessage=async(agentType,message)=>{if(!isConnected){throw new Error('Not connected to server');}try{const response=await axios.post(`${apiUrl}/api/agents/${agentType}/message`,{message,sender:'frontend'});return response.data;}catch(error){console.error(`Failed to send message to agent ${agentType}:`,error);throw error;}};/**
   * Register an agent with the orchestrator.
   * 
   * @param agentType Type of the agent to register
   */const registerAgent=agentType=>{setAgents(prevAgents=>({...prevAgents,[agentType]:true}));};/**
   * Unregister an agent from the orchestrator.
   * 
   * @param agentType Type of the agent to unregister
   */const unregisterAgent=agentType=>{setAgents(prevAgents=>{const newAgents={...prevAgents};delete newAgents[agentType];return newAgents;});};// Context value
const contextValue={serverPort,frontendPort,apiUrl,isConnected,agents,sendMessage,registerAgent,unregisterAgent};return/*#__PURE__*/_jsx(OrchestratorContext.Provider,{value:contextValue,children:children});};/**
 * Hook for accessing the orchestrator context.
 * 
 * @returns Orchestrator context
 * @throws Error if used outside of an OrchestratorProvider
 */export const useOrchestratorContext=()=>{const context=useContext(OrchestratorContext);if(context===null){throw new Error('useOrchestratorContext must be used within an OrchestratorProvider');}return context;};